"use client";

import { useActionState, useEffect, useState } from "react";
import { useFormStatus } from "react-dom";
import { deleteOrder, updateOrderStatus } from "./actions";

type StatusUpdateFormProps = {
  orderId: string;
  defaultStatus: string;
  orderNumber: string;
};

type ActionState = {
  ok?: boolean;
  message?: string;
};

const initialState: ActionState = {};

function SubmitButton() {
  const { pending } = useFormStatus();

  return (
    <button
      type="submit"
      disabled={pending}
      className="rounded-full bg-[var(--primary)] px-5 py-3 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {pending ? "Enregistrement..." : "Enregistrer"}
    </button>
  );
}

export function StatusUpdateForm({
  orderId,
  defaultStatus,
  orderNumber,
}: StatusUpdateFormProps) {
  const [state, formAction] = useActionState(updateOrderStatus, initialState);
  const [showToast, setShowToast] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (!state?.message) return;

    setShowToast(true);

    const timer = window.setTimeout(() => {
      setShowToast(false);
    }, 2200);

    return () => window.clearTimeout(timer);
  }, [state]);

  async function handleDelete() {
    try {
      setDeleteLoading(true);

      const formData = new FormData();
      formData.append("orderId", orderId);

      const result = await deleteOrder(formData);

      if (!result?.ok) {
        throw new Error(result?.message || "Impossible de supprimer la commande.");
      }

      window.location.reload();
    } catch (error) {
      console.error(error);
      setShowToast(true);
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  }

  return (
    <>
      <form action={formAction} className="grid gap-3">
        <input type="hidden" name="orderId" value={orderId} />

        <label className="text-sm font-medium text-[var(--foreground)]/78">
          Mettre à jour le statut
        </label>

        <select
          key={defaultStatus}
          name="status"
          defaultValue={defaultStatus}
          className="rounded-2xl border border-[var(--border)] bg-white/70 px-4 py-3 outline-none transition focus:border-[var(--primary)]"
        >
          <option value="PENDING">En attente</option>
          <option value="PAID">Payée</option>
          <option value="PREPARING">En préparation</option>
          <option value="SHIPPED">Expédiée</option>
          <option value="COMPLETED">Terminée</option>
          <option value="CANCELLED">Annulée</option>
        </select>

        <SubmitButton />
      </form>

      <button
        type="button"
        onClick={() => setShowDeleteModal(true)}
        disabled={deleteLoading}
        className="mt-3 inline-flex w-full items-center justify-center rounded-full border border-red-200 bg-red-50 px-5 py-3 text-sm font-medium text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {deleteLoading ? "Suppression..." : "Supprimer la commande"}
      </button>

      {showDeleteModal ? (
        <div className="fixed inset-0 z-[140] flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="Fermer la modale"
            className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
            onClick={() => {
              if (!deleteLoading) setShowDeleteModal(false);
            }}
          />

          <div className="relative w-full max-w-md rounded-[28px] border border-[var(--border)] bg-[var(--card)] p-6 shadow-[var(--shadow)] sm:p-7">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-red-600/80">
              Confirmation
            </p>

            <h3 className="mt-3 text-2xl leading-tight">
              Supprimer cette commande ?
            </h3>

            <p className="mt-4 text-sm leading-7 text-[var(--foreground)]/72">
              Cette action supprimera définitivement la commande{" "}
              <span className="font-medium text-[var(--foreground)]">
                {orderNumber}
              </span>
              . Cette opération est irréversible.
            </p>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleteLoading}
                className="rounded-full border border-[var(--border)] px-5 py-3 text-sm font-medium transition hover:border-[var(--primary)] hover:text-[var(--primary)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                Annuler
              </button>

              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteLoading}
                className="rounded-full border border-red-200 bg-red-50 px-5 py-3 text-sm font-medium text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deleteLoading ? "Suppression..." : "Confirmer la suppression"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showToast && state?.message ? (
        <div
          className={`fixed bottom-6 right-6 z-[120] rounded-2xl border px-4 py-3 text-sm shadow-[var(--shadow)] backdrop-blur-md transition ${
            state.ok
              ? "border-emerald-200 bg-emerald-50/95 text-emerald-700"
              : "border-red-200 bg-red-50/95 text-red-600"
          }`}
          aria-live="polite"
        >
          {state.message}
        </div>
      ) : null}
    </>
  );
}